import React from 'react';
import { Link } from 'react-router-dom';
import { CookieSettingsLink } from '@digiwedge/cmp-consent-react';
import { Linkedin, Twitter } from 'lucide-react';
import logo from '../../assets/yolo-logo.png';
import { SITE_CONFIG, resolveUrl } from '../siteConfig';
import './Footer.css';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const quoteUrl = resolveUrl(SITE_CONFIG.urls.quote);
  const assessmentUrl = resolveUrl(SITE_CONFIG.urls.assessment);
  const supportUrl = resolveUrl(SITE_CONFIG.urls.support);
  const featureUrl = resolveUrl(SITE_CONFIG.urls.featureRequests);
  const voiceProUrl = resolveUrl(SITE_CONFIG.urls.voicePro);
  const mspAiUrl = resolveUrl(SITE_CONFIG.urls.mspAi);
  const linkedinUrl = resolveUrl(SITE_CONFIG.urls.linkedin);
  const twitterUrl = resolveUrl(SITE_CONFIG.urls.twitter);
  const externalProps = (url: string) =>
    url !== '#' ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  const socialLinks = [
    { label: 'LinkedIn', url: linkedinUrl, icon: Linkedin },
    { label: 'X (Twitter)', url: twitterUrl, icon: Twitter },
  ];

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          {/* Brand Column */}
          <div className="footer-brand">
            <img
              src={logo || undefined}
              alt="YOLO Telecoms"
              className="footer-logo footer-logo--mono"
            />
            <p className="footer-tagline">{SITE_CONFIG.brand.tagline}</p>
          <div className="footer-actions">
            <a href={assessmentUrl} className="btn btn-white btn-sm" {...externalProps(assessmentUrl)}>
              Free Assessment
            </a>
            <a href={quoteUrl} className="btn btn-outline btn-sm" {...externalProps(quoteUrl)}>
              Get a Quote
            </a>
          </div>
          <div className="footer-social">
            {socialLinks.map(({ label, url, icon: Icon }) => (
              <a
                key={label}
                href={url}
                aria-label={label}
                title={label}
                className="footer-social-link"
                {...externalProps(url)}
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

          {/* Services Column */}
          <div className="footer-column">
            <h4>Services</h4>
            <ul>
              <li><Link to="/services/managed-services">Managed IT</Link></li>
              <li><Link to="/services/connectivity">Connectivity</Link></li>
              <li><Link to="/services/voice">Voice Solutions</Link></li>
              <li><Link to="/services/security">Security</Link></li>
              <li><Link to="/services/cloud">Cloud Services</Link></li>
              <li><Link to="/services/backup">Backup & Recovery</Link></li>
            </ul>
          </div>

          {/* Products Column */}
          <div className="footer-column">
            <h4>Products</h4>
            <ul>
              <li><Link to="/products/voicepro">VoicePro</Link></li>
              <li><Link to="/products/msp-ai">MSP-AI</Link></li>
              <li><Link to="/specials">Hardware Specials</Link></li>
              <li><Link to="/roadmap">Roadmap</Link></li>
            </ul>
          </div>

          {/* Company Column */}
          <div className="footer-column">
            <h4>Company</h4>
            <ul>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/partners">Partners</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/blog">Insights</Link></li>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="footer-column">
            <h4>Contact</h4>
            <address className="footer-contact">
              <p>
                {SITE_CONFIG.brand.location}<br />
                South Africa
              </p>
              <p><a href={supportUrl} {...externalProps(supportUrl)}>Support</a></p>
              <p><a href={featureUrl} {...externalProps(featureUrl)}>Feature Requests</a></p>
            </address>
          </div>
        </div>

        {/* Partners */}
        <div className="footer-partners">
          <p>Core Platforms</p>
          <div className="partner-logos">
            <span>3CX</span>
            <span>HaloPSA</span>
            <span>Acronis</span>
            <span>Microsoft 365</span>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p>&copy; {currentYear} YOLO Telecoms. All rights reserved.</p>
          <div className="footer-legal">
            <a href={voiceProUrl} {...externalProps(voiceProUrl)}>VoicePro Portal</a>
            <a href={mspAiUrl} {...externalProps(mspAiUrl)}>MSP-AI Portal</a>
            <Link to="/roadmap">Roadmap</Link>
            <CookieSettingsLink className="footer-cookie-link">
              Cookie Settings
            </CookieSettingsLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
